
import React from 'react';
import { pricingTiers } from '@/content/mvp';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Users, Star } from 'lucide-react';
import { cn } from '@/lib/utils';

type PricingTiersProps = {
    className?: string;
    hideButtons?: boolean;
    currentTier?: string;
    inLanding?: boolean;
};

const getTierIcon = (tier: string) => {
    switch (tier) {
        case 'free':
            return <CreditCard className="h-5 w-5" />;
        case 'starter':
            return <Star className="h-5 w-5" />;
        case 'team':
            return <Users className="h-5 w-5" />;
        default:
            return <CreditCard className="h-5 w-5" />;
    }
};



export const PricingTiers: React.FC<PricingTiersProps> = ({ className, hideButtons = false, currentTier, inLanding = false }) => {
    return (
        <section className={cn('w-full px-0 py-20', className)}>
            <div className="mb-12 text-center">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Simple, scalable pricing</h2>
                <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                    Start free, then grow into advanced timelines, replay filters, and collaboration features as your hiring ramps.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pricingTiers.map((plan) => {
                    const tierName = plan.name.toLowerCase();
                    const isCurrent = currentTier === tierName;
                    return (
                        <Card
                            key={plan.name}
                            className={cn(
                                'relative flex flex-col',
                                isCurrent ? 'ring-2 ring-blue-500' : ''
                            )}
                        >
                            {isCurrent && (
                                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                                    <Badge className="bg-blue-500 hover:bg-blue-500 dark:bg-blue-600 dark:hover:bg-blue-600">Current Plan</Badge>
                                </div>
                            )}

                            <CardHeader className="text-center">
                                <div className="flex justify-center mb-2">
                                    {getTierIcon(tierName)}
                                </div>
                                <CardTitle className="text-xl">{plan.name}</CardTitle>
                                <CardDescription>{plan.blurb}</CardDescription>
                                <div className="space-y-1">
                                    <div className="text-3xl font-bold">{plan.price}</div>
                                    <div className="h-5">{/* Optionally add interval info if present in mvp.ts */}</div>
                                </div>
                            </CardHeader>

                            <CardContent className="flex flex-col flex-1">
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm flex-1">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-500 dark:bg-green-400 rounded-full mt-2 flex-shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {!hideButtons && (
                                    <div className="mt-6 space-y-4">
                                        <Separator />
                                        <div className="space-y-2">
                                            {isCurrent ? (
                                                <Button variant="outline" className="w-full" disabled>
                                                    Current Plan
                                                </Button>
                                            ) : currentTier === 'free' || !currentTier ? (
                                                inLanding ? (
                                                    <Button asChild className="w-full">
                                                        <Link to="/register">Get Started</Link>
                                                    </Button>
                                                ) : (
                                                    <Button asChild className="w-full">
                                                        <Link to={`/app/billing/subscribe/${tierName}`}>Get Started</Link>
                                                    </Button>
                                                )
                                            ) : currentTier === 'starter' ? (
                                                tierName === 'free' ? (
                                                    <Button variant="outline" className="w-full" disabled>
                                                        Use 'Manage Billing' to downgrade
                                                    </Button>
                                                ) : (
                                                    <Button asChild className="w-full">
                                                        <Link to={`/app/billing/subscribe/${tierName}`}>Upgrade</Link>
                                                    </Button>
                                                )
                                            ) : currentTier === 'team' ? (
                                                <Button variant="outline" className="w-full" disabled>
                                                    Use 'Manage Billing' to downgrade
                                                </Button>
                                            ) : (
                                                <Button asChild className="w-full">
                                                    <Link to={`/app/billing/subscribe/${tierName}`}>Switch Plan</Link>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </section>
    );
};

export default PricingTiers;
